import React, { useState, useEffect } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { ArrowLeft, Check, Lock, Coins, ShoppingCart, Loader2, AlertCircle, Crosshair, Palette, Trophy, Star, Sparkles, Gem } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { createPurchaseTransaction, sendAndConfirmTransaction } from '../utils/solanaTransactions';
import axios from 'axios';
import NFTCharacterPreview from './NFTCharacterPreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RARITY_COLORS = {
  common: '#A1A1AA',
  uncommon: '#22C55E',
  rare: '#3B82F6',
  epic: '#A855F7',
  legendary: '#F59E0B'
};

const Shop = ({ 
  skins = [], 
  weapons = [],
  nfts = [],
  ownedSkins = [], 
  ownedWeapons = [],
  ownedNfts = [],
  selectedSkinId,
  selectedNftId,
  onSelectSkin,
  onSelectNft,
  onPurchase, 
  onBack,
  tokenBalance = 0,
  isLoading = false,
  onRefreshBalance,
  playerStats = null
}) => {
  const { publicKey, signTransaction } = useWallet();
  const { connection } = useConnection();
  const [purchasingId, setPurchasingId] = useState(null);
  const [txStatus, setTxStatus] = useState('');
  const [activeTab, setActiveTab] = useState('skins');
  const [achievements, setAchievements] = useState([]);
  const [playerProgress, setPlayerProgress] = useState({
    highestLevel: 0,
    highestScore: 0,
    totalKills: 0,
    totalGames: 0
  });

  // Fetch achievements on mount
  useEffect(() => {
    const fetchAchievements = async () => {
      if (publicKey) {
        try {
          const response = await axios.get(`${API}/player/achievements/${publicKey.toBase58()}`);
          setAchievements(response.data.achievements || []);
          setPlayerProgress({
            highestLevel: response.data.highest_level || 0,
            highestScore: response.data.highest_score || 0,
            totalKills: response.data.total_kills || 0,
            totalGames: response.data.total_games || 0
          });
        } catch (error) {
          console.error('Error fetching achievements:', error);
        }
      }
    };
    fetchAchievements();
  }, [publicKey]);

  // Helper to get achievement unlock hint based on requirement type
  const getAchievementHint = (item) => {
    const req = item.unlock_requirement;
    if (!req) return '';
    
    if (req.startsWith('level_')) return `Reach Level ${item.unlock_level} to unlock`;
    if (req.startsWith('kills_')) return `Reach ${item.unlock_kills?.toLocaleString()} kills to unlock`;
    if (req.startsWith('games_')) return `Play ${item.unlock_games} games to unlock`;
    if (req.startsWith('score_')) return `Score ${item.unlock_score?.toLocaleString()} points to unlock`;
    if (req.startsWith('bosses_')) return `Beat ${item.unlock_level} bosses to unlock`;
    
    return 'Complete achievement to unlock';
  };

  // Get progress value based on achievement requirement type
  const getProgressValue = (item) => {
    const req = item.unlock_requirement;
    if (!req) return 0;
    
    if (req.startsWith('level_') || req.startsWith('bosses_')) return playerProgress.highestLevel;
    if (req.startsWith('kills_')) return playerProgress.totalKills;
    if (req.startsWith('games_')) return playerProgress.totalGames;
    if (req.startsWith('score_')) return playerProgress.highestScore;
    
    return 0;
  };

  // Get requirement value based on achievement type
  const getRequirementValue = (item) => {
    if (item.unlock_level) return item.unlock_level;
    if (item.unlock_kills) return item.unlock_kills;
    if (item.unlock_games) return item.unlock_games;
    if (item.unlock_score) return item.unlock_score;
    return 20; // Default fallback
  };

  // Get short button text for locked achievements
  const getAchievementButtonText = (item) => {
    const req = item.unlock_requirement;
    if (!req) return 'LOCKED';
    
    if (req.startsWith('level_')) return `LEVEL ${item.unlock_level}`;
    if (req.startsWith('kills_')) return `${item.unlock_kills} KILLS`;
    if (req.startsWith('games_')) return `${item.unlock_games} GAMES`;
    if (req.startsWith('score_')) return `${(item.unlock_score / 1000).toFixed(0)}K SCORE`;
    if (req.startsWith('bosses_')) return `${item.unlock_level} BOSSES`;
    
    return 'LOCKED';
  };

  const handlePurchaseSkin = async (skin) => {
    if (!publicKey || !signTransaction) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (tokenBalance < skin.price) {
      toast.error(`Insufficient balance. You need ${skin.price} SML tokens`);
      return;
    }

    setPurchasingId(skin.id);
    setTxStatus('Preparing transaction...');

    try {
      const purchaseInfoRes = await axios.get(`${API}/shop/purchase-info/${skin.id}`);
      const { treasury_wallet, decimals } = purchaseInfoRes.data;

      setTxStatus('Creating transaction...');

      const transaction = await createPurchaseTransaction(
        connection,
        publicKey,
        treasury_wallet,
        skin.price,
        decimals
      );

      setTxStatus('Please approve in your wallet...');

      const signedTransaction = await signTransaction(transaction);

      setTxStatus('Sending transaction...');

      const signature = await sendAndConfirmTransaction(connection, signedTransaction);

      setTxStatus('Verifying purchase...');

      await axios.post(`${API}/shop/verify-purchase`, {
        wallet_address: publicKey.toBase58(),
        skin_id: skin.id,
        transaction_signature: signature
      });

      toast.success(`Successfully purchased ${skin.name}!`, {
        description: `Transaction: ${signature.slice(0, 8)}...`
      });

      if (onRefreshBalance) onRefreshBalance();
      if (onPurchase) onPurchase(skin);

    } catch (error) {
      console.error('Purchase error:', error);
      
      if (error.message?.includes('User rejected')) {
        toast.error('Transaction cancelled');
      } else if (error.message?.includes('Insufficient')) {
        toast.error('Insufficient token balance');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Purchase failed. Please try again.');
      }
    } finally {
      setPurchasingId(null);
      setTxStatus('');
    }
  };

  const handlePurchaseWeapon = async (weapon) => {
    if (!publicKey || !signTransaction) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (tokenBalance < weapon.price) {
      toast.error(`Insufficient balance. You need ${weapon.price} SML tokens`);
      return;
    }

    setPurchasingId(weapon.id);
    setTxStatus('Preparing transaction...');

    try {
      const purchaseInfoRes = await axios.get(`${API}/shop/purchase-info/weapon/${weapon.id}`);
      const { treasury_wallet, decimals } = purchaseInfoRes.data;

      setTxStatus('Creating transaction...');

      const transaction = await createPurchaseTransaction(
        connection,
        publicKey,
        treasury_wallet,
        weapon.price,
        decimals
      );

      setTxStatus('Please approve in your wallet...');

      const signedTransaction = await signTransaction(transaction);

      setTxStatus('Sending transaction...');

      const signature = await sendAndConfirmTransaction(connection, signedTransaction);

      setTxStatus('Verifying purchase...');

      await axios.post(`${API}/shop/purchase-weapon`, {
        wallet_address: publicKey.toBase58(),
        weapon_id: weapon.id,
        transaction_signature: signature
      });

      toast.success(`Successfully purchased ${weapon.name}!`, {
        description: `Ammo loaded! Use Q key to switch weapons in-game.`
      });

      if (onRefreshBalance) onRefreshBalance();
      if (onPurchase) onPurchase(weapon);

    } catch (error) {
      console.error('Purchase error:', error);
      
      if (error.message?.includes('User rejected')) {
        toast.error('Transaction cancelled');
      } else if (error.message?.includes('Insufficient')) {
        toast.error('Insufficient token balance');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Purchase failed. Please try again.');
      }
    } finally {
      setPurchasingId(null);
      setTxStatus('');
    }
  };

  const handlePurchaseNft = async (nft) => {
    if (!publicKey || !signTransaction) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (tokenBalance < nft.price) {
      toast.error(`Insufficient balance. You need ${nft.price} SML tokens`);
      return;
    }

    setPurchasingId(nft.id);
    setTxStatus('Preparing NFT mint...');

    try {
      const purchaseInfoRes = await axios.get(`${API}/shop/purchase-info/nft/${nft.id}`);
      const { treasury_wallet, decimals, remaining_supply } = purchaseInfoRes.data;

      if (remaining_supply <= 0) {
        toast.error('This NFT character is sold out!');
        setPurchasingId(null);
        setTxStatus('');
        return;
      }

      setTxStatus('Creating transaction...');

      const transaction = await createPurchaseTransaction(
        connection,
        publicKey,
        treasury_wallet,
        nft.price,
        decimals
      );

      setTxStatus('Please approve in your wallet...');

      const signedTransaction = await signTransaction(transaction);

      setTxStatus('Sending transaction...');

      const signature = await sendAndConfirmTransaction(connection, signedTransaction);

      setTxStatus('Minting your NFT character...');

      const result = await axios.post(`${API}/shop/purchase-nft`, {
        wallet_address: publicKey.toBase58(),
        nft_id: nft.id,
        transaction_signature: signature
      });

      toast.success(`Welcome, ${nft.name}!`, {
        description: `Edition #${result.data.edition_number} of ${result.data.total_supply} - Now in your collection!`
      });

      if (onRefreshBalance) onRefreshBalance();
      if (onPurchase) onPurchase(nft);

    } catch (error) {
      console.error('NFT Purchase error:', error);
      
      if (error.message?.includes('User rejected')) {
        toast.error('Transaction cancelled');
      } else if (error.message?.includes('Insufficient')) {
        toast.error('Insufficient token balance');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('NFT purchase failed. Please try again.');
      }
    } finally {
      setPurchasingId(null);
      setTxStatus('');
    }
  };

  const handleSelect = (skin) => {
    if (ownedSkins.includes(skin.id)) {
      onSelectSkin(skin);
      toast.success(`Selected ${skin.name}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={onBack}
            variant="ghost"
            className="text-[#A1A1AA] hover:text-white"
            data-testid="shop-back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-2 glass-card px-4 py-2">
            <Coins className="w-5 h-5 text-[#FFD700]" />
            <span className="font-game text-[#FFD700]" data-testid="shop-balance">
              {tokenBalance.toLocaleString()} SML
            </span>
          </div>
        </div>

        {/* Title */}
        <h1 className="font-game text-3xl md:text-4xl text-[#14F195] text-center mb-2 neon-text">
          SHOP
        </h1>
        <p className="text-[#A1A1AA] text-center mb-4">
          Purchase items with SML tokens on Solana
        </p>
        
        {/* Real Transaction Notice */}
        <div className="glass-card p-3 mb-6 flex items-center justify-center gap-2 text-sm max-w-md mx-auto">
          <AlertCircle className="w-4 h-4 text-[#14F195]" />
          <span className="text-[#A1A1AA]">
            Purchases use <span className="text-[#14F195]">real Solana transactions</span>
          </span>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-8 flex-wrap">
          <button
            onClick={() => setActiveTab('skins')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-game text-sm transition-all ${
              activeTab === 'skins' 
                ? 'bg-[#9945FF] text-white' 
                : 'bg-[#1a1a1a] text-[#A1A1AA] hover:bg-[#2a2a2a]'
            }`}
            data-testid="skins-tab"
          >
            <Palette className="w-4 h-4" />
            SKINS
          </button>
          <button
            onClick={() => setActiveTab('weapons')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-game text-sm transition-all ${
              activeTab === 'weapons' 
                ? 'bg-[#FF4444] text-white' 
                : 'bg-[#1a1a1a] text-[#A1A1AA] hover:bg-[#2a2a2a]'
            }`}
            data-testid="weapons-tab"
          >
            <Crosshair className="w-4 h-4" />
            WEAPONS
          </button>
          <button
            onClick={() => setActiveTab('nfts')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-game text-sm transition-all ${
              activeTab === 'nfts' 
                ? 'bg-gradient-to-r from-[#9945FF] to-[#14F195] text-white' 
                : 'bg-[#1a1a1a] text-[#A1A1AA] hover:bg-[#2a2a2a]'
            }`}
            data-testid="nfts-tab"
          >
            <Gem className="w-4 h-4" />
            NFT CHARACTERS
          </button>
          <button
            onClick={() => setActiveTab('achievements')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-game text-sm transition-all ${
              activeTab === 'achievements' 
                ? 'bg-[#FFD700] text-black' 
                : 'bg-[#1a1a1a] text-[#A1A1AA] hover:bg-[#2a2a2a]'
            }`}
            data-testid="achievements-tab"
          >
            <Trophy className="w-4 h-4" />
            ACHIEVEMENTS
          </button>
        </div>

        {/* Skins Grid */}
        {activeTab === 'skins' && (
          <>
            {skins.some(s => s.price > 0) && (
              <div className="mb-4 px-4 py-2 bg-[#0a1a2a] border border-[#1e3a5f] rounded-lg text-sm text-[#A1A1AA]" data-testid="skin-pricing-hint">
                All paid skins priced at <span className="font-game text-[#FFD700]">$5.00 USD</span> (price in tokens updates with market rate)
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {skins.map((skin) => {
              const isOwned = ownedSkins.includes(skin.id);
              const isSelected = selectedSkinId === skin.id;
              const canAfford = tokenBalance >= skin.price;
              const isPurchasing = purchasingId === skin.id;
              const isAchievement = skin.type === 'achievement';
              const achievementData = achievements.find(a => a.reward_id === skin.id);
              const unlockProgress = achievementData ? achievementData.progress : getProgressValue(skin);
              const unlockRequirement = getRequirementValue(skin);
              const isUnlocked = isOwned || (achievementData && achievementData.unlocked);

              return (
                <div
                  key={skin.id}
                  className={`shop-card ${isOwned ? 'owned' : ''} ${isSelected ? 'selected' : ''} ${isAchievement && !isUnlocked ? 'achievement-locked' : ''}`}
                  data-testid={`skin-card-${skin.id}`}
                >
                  <div 
                    className="aspect-square relative overflow-hidden"
                    style={{ backgroundColor: skin.color + '15' }}
                  >
                    {skin.image_url ? (
                      <img
                        src={skin.image_url}
                        alt={skin.name}
                        className={`w-full h-full object-cover ${isAchievement && !isUnlocked ? 'opacity-30 grayscale' : 'opacity-80'}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative">
                          <div 
                            className="w-12 h-20 relative"
                            style={{ filter: 'drop-shadow(0 0 10px ' + skin.color + ')' }}
                          >
                            <div 
                              className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-8 rounded"
                              style={{ backgroundColor: '#FFCC99' }}
                            >
                              <div className="absolute top-2 left-1 w-1.5 h-1.5 bg-black rounded-full" />
                              <div className="absolute top-2 right-1 w-1.5 h-1.5 bg-black rounded-full" />
                              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-4 h-1 bg-black rounded" />
                            </div>
                            <div 
                              className="absolute top-8 left-1/2 -translate-x-1/2 w-10 h-8 rounded"
                              style={{ backgroundColor: skin.color }}
                            />
                            <div 
                              className="absolute bottom-0 left-2 w-3 h-5 rounded"
                              style={{ backgroundColor: skin.color, filter: 'brightness(0.8)' }}
                            />
                            <div 
                              className="absolute bottom-0 right-2 w-3 h-5 rounded"
                              style={{ backgroundColor: skin.color, filter: 'brightness(0.8)' }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Achievement badge */}
                    {isAchievement && (
                      <div className="absolute top-2 left-2 bg-[#FFD700] text-black px-2 py-1 rounded text-xs font-game flex items-center gap-1">
                        <Trophy className="w-3 h-3" />
                        ACHIEVEMENT
                      </div>
                    )}

                    <div 
                      className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-game uppercase"
                      style={{ 
                        backgroundColor: RARITY_COLORS[skin.rarity] + '30',
                        color: RARITY_COLORS[skin.rarity],
                        border: '1px solid ' + RARITY_COLORS[skin.rarity]
                      }}
                    >
                      {skin.rarity}
                    </div>

                    {isSelected && (
                      <div className="absolute bottom-2 left-2 bg-[#14F195] text-black px-2 py-1 rounded text-xs font-game">
                        EQUIPPED
                      </div>
                    )}

                    {/* Lock overlay for achievement skins */}
                    {isAchievement && !isUnlocked && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <Lock className="w-12 h-12 text-[#FFD700]" />
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <h3 className="font-game text-sm text-white mb-1">{skin.name}</h3>
                    <p className="text-[#A1A1AA] text-xs mb-3 line-clamp-2">{skin.description}</p>

                    {/* Achievement progress bar */}
                    {isAchievement && !isUnlocked && (
                      <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-[#FFD700]">Progress</span>
                          <span className="text-[#A1A1AA]">{unlockProgress.toLocaleString()}/{unlockRequirement.toLocaleString()}</span>
                        </div>
                        <div className="w-full bg-[#333] rounded-full h-2">
                          <div 
                            className="bg-[#FFD700] h-2 rounded-full transition-all"
                            style={{ width: `${Math.min((unlockProgress / unlockRequirement) * 100, 100)}%` }}
                          />
                        </div>
                        <p className="text-[#A1A1AA] text-[10px] mt-1 text-center">
                          {getAchievementHint(skin)}
                        </p>
                      </div>
                    )}

                    {isOwned || isUnlocked ? (
                      <Button
                        onClick={() => handleSelect(skin)}
                        className={`w-full ${isSelected 
                          ? 'bg-[#14F195] text-black hover:bg-[#14F195]/90' 
                          : 'bg-transparent border-[#9945FF] text-[#9945FF] hover:bg-[#9945FF]/10'
                        }`}
                        data-testid={`select-skin-${skin.id}`}
                      >
                        {isSelected ? (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            EQUIPPED
                          </>
                        ) : (
                          'SELECT'
                        )}
                      </Button>
                    ) : isAchievement ? (
                      <div className="w-full py-2 px-4 bg-[#FFD700]/20 border border-[#FFD700] rounded text-center">
                        <span className="text-[#FFD700] font-game text-xs flex items-center justify-center gap-2">
                          <Star className="w-4 h-4" />
                          {getAchievementButtonText(skin)}
                        </span>
                      </div>
                    ) : skin.price === 0 ? (
                      <Button
                        onClick={() => handleSelect(skin)}
                        className="w-full bg-[#14F195] text-black hover:bg-[#14F195]/90"
                        data-testid={`select-skin-${skin.id}`}
                      >
                        FREE
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <Button
                          onClick={() => handlePurchaseSkin(skin)}
                          disabled={!canAfford || isPurchasing || isLoading || !publicKey}
                          className={`w-full ${canAfford && publicKey
                            ? 'bg-[#14F195] text-black hover:bg-[#14F195]/90' 
                            : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          }`}
                          data-testid={`buy-skin-${skin.id}`}
                        >
                          {isPurchasing ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              PROCESSING...
                            </>
                          ) : canAfford ? (
                            <>
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              {skin.price} SML
                            </>
                          ) : (
                            <>
                              <Lock className="w-4 h-4 mr-2" />
                              {skin.price} SML
                            </>
                          )}
                        </Button>
                        {isPurchasing && txStatus && (
                          <p className="text-xs text-[#A1A1AA] text-center animate-pulse">
                            {txStatus}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </>
        )}

        {/* Weapons Grid */}
        {activeTab === 'weapons' && (
          <>
            <div className="glass-card p-4 mb-6 text-center">
              <p className="text-[#A1A1AA] text-sm">
                <span className="text-[#14F195] font-game">ONE-TIME PURCHASE:</span> Each weapon is permanently owned and loaded at the start of every game.
                Use <span className="text-[#14F195] font-game">Q</span> to switch weapons in-game.
              </p>
              <p className="text-[#14F195] text-xs mt-2">
                All weapons priced at <span className="font-game">$5.00 USD</span> (price in tokens updates with market rate)
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {weapons.map((weapon) => {
                const isOwned = ownedWeapons.includes(weapon.id);
                const canAfford = tokenBalance >= weapon.price;
                const isPurchasing = purchasingId === weapon.id;
                const isAchievementWeapon = weapon.type === 'achievement_weapon';
                const achievementData = achievements.find(a => a.reward_id === weapon.id);
                const unlockProgress = achievementData ? achievementData.progress : getProgressValue(weapon);
                const unlockRequirement = getRequirementValue(weapon);
                const isUnlocked = isOwned || (achievementData && achievementData.unlocked);

                return (
                  <div
                    key={weapon.id}
                    className={`shop-card ${isOwned ? 'owned' : ''} ${isAchievementWeapon && !isUnlocked ? 'achievement-locked' : ''}`}
                    data-testid={`weapon-card-${weapon.id}`}
                  >
                    <div 
                      className="aspect-video relative overflow-hidden"
                      style={{ backgroundColor: weapon.color + '15' }}
                    >
                      {weapon.image_url ? (
                        <img
                          src={weapon.image_url}
                          alt={weapon.name}
                          className={`w-full h-full object-cover ${isAchievementWeapon && !isUnlocked ? 'opacity-30 grayscale' : 'opacity-60'}`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : null}
                      
                      {/* Weapon icon overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div 
                          className={`text-6xl ${isAchievementWeapon && !isUnlocked ? 'opacity-30' : ''}`}
                          style={{ filter: 'drop-shadow(0 0 15px ' + weapon.color + ')' }}
                        >
                          {weapon.id === 'rocket_launcher' ? '🚀' :
                           weapon.id === 'machine_gun' ? '🔫' :
                           weapon.id === 'mini_tank' ? '🛡️' :
                           weapon.id === 'assault_rifle' ? '🎯' :
                           weapon.id === 'grenade' ? '💣' :
                           weapon.id === 'laser_rifle' ? '⚡' : '💥'}
                        </div>
                      </div>

                      {/* Achievement badge for achievement weapons */}
                      {isAchievementWeapon && (
                        <div className="absolute top-2 left-2 bg-[#FFD700] text-black px-2 py-1 rounded text-xs font-game flex items-center gap-1">
                          <Trophy className="w-3 h-3" />
                          ACHIEVEMENT
                        </div>
                      )}

                      {/* USD Price Badge - only for purchasable weapons */}
                      {!isAchievementWeapon && (
                        <div className="absolute top-2 left-2 px-2 py-1 rounded text-xs font-game bg-[#14F195] text-black">
                          $5.00 USD
                        </div>
                      )}

                      <div 
                        className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-game uppercase"
                        style={{ 
                          backgroundColor: RARITY_COLORS[weapon.rarity] + '30',
                          color: RARITY_COLORS[weapon.rarity],
                          border: '1px solid ' + RARITY_COLORS[weapon.rarity]
                        }}
                      >
                        {weapon.rarity}
                      </div>

                      {/* Lock overlay for achievement weapons */}
                      {isAchievementWeapon && !isUnlocked && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                          <Lock className="w-12 h-12 text-[#FFD700]" />
                        </div>
                      )}

                      {isOwned && (
                        <div className="absolute top-2 left-2 bg-[#14F195] text-black px-2 py-1 rounded text-xs font-game">
                          READY
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      <h3 className="font-game text-sm text-white mb-1">{weapon.name}</h3>
                      <p className="text-[#A1A1AA] text-xs mb-3 line-clamp-2">{weapon.description}</p>
                      
                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
                        <div className="flex justify-between">
                          <span className="text-[#A1A1AA]">Damage:</span>
                          <span className="text-[#FF4444] font-game">{weapon.damage}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#A1A1AA]">Ammo:</span>
                          <span className="text-[#14F195] font-game">{weapon.ammo}</span>
                        </div>
                        {weapon.blast_radius && (
                          <div className="flex justify-between col-span-2">
                            <span className="text-[#A1A1AA]">Blast Radius:</span>
                            <span className="text-[#FF9500] font-game">{weapon.blast_radius}px</span>
                          </div>
                        )}
                        {weapon.armor_boost && (
                          <div className="flex justify-between col-span-2">
                            <span className="text-[#A1A1AA]">Armor Boost:</span>
                            <span className="text-[#5AC8FA] font-game">+{weapon.armor_boost}</span>
                          </div>
                        )}
                        {weapon.piercing && (
                          <div className="flex justify-between col-span-2">
                            <span className="text-[#A1A1AA]">Special:</span>
                            <span className="text-[#00FFFF] font-game">Piercing</span>
                          </div>
                        )}
                      </div>

                      {/* Achievement progress bar for achievement weapons */}
                      {isAchievementWeapon && !isUnlocked && (
                        <div className="mb-3">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-[#FFD700]">Progress</span>
                            <span className="text-[#A1A1AA]">{unlockProgress.toLocaleString()}/{unlockRequirement.toLocaleString()}</span>
                          </div>
                          <div className="w-full bg-[#333] rounded-full h-2">
                            <div 
                              className="bg-[#FFD700] h-2 rounded-full transition-all"
                              style={{ width: `${Math.min((unlockProgress / unlockRequirement) * 100, 100)}%` }}
                            />
                          </div>
                          <p className="text-[#A1A1AA] text-[10px] mt-1 text-center">
                            {getAchievementHint(weapon)}
                          </p>
                        </div>
                      )}

                      {isOwned || isUnlocked ? (
                        <div className="w-full py-2 px-4 bg-[#14F195]/20 border border-[#14F195] rounded text-center">
                          <span className="text-[#14F195] font-game text-sm">
                            {isAchievementWeapon ? 'UNLOCKED' : 'AMMO LOADED'}
                          </span>
                        </div>
                      ) : isAchievementWeapon ? (
                        <div className="w-full py-2 px-4 bg-[#FFD700]/20 border border-[#FFD700] rounded text-center">
                          <span className="text-[#FFD700] font-game text-xs flex items-center justify-center gap-2">
                            <Star className="w-4 h-4" />
                            {getAchievementButtonText(weapon)}
                          </span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Button
                            onClick={() => handlePurchaseWeapon(weapon)}
                            disabled={!canAfford || isPurchasing || isLoading || !publicKey}
                            className={`w-full ${canAfford && publicKey
                              ? 'bg-[#FF4444] text-white hover:bg-[#FF4444]/90' 
                              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            }`}
                            data-testid={`buy-weapon-${weapon.id}`}
                          >
                            {isPurchasing ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                PROCESSING...
                              </>
                            ) : canAfford ? (
                              <>
                                <ShoppingCart className="w-4 h-4 mr-2" />
                                {weapon.price.toLocaleString()} SML
                              </>
                            ) : (
                              <>
                                <Lock className="w-4 h-4 mr-2" />
                                {weapon.price.toLocaleString()} SML
                              </>
                            )}
                          </Button>
                          <p className="text-[10px] text-[#A1A1AA] text-center">
                            ≈ $5.00 USD at current rate
                          </p>
                          {isPurchasing && txStatus && (
                            <p className="text-xs text-[#A1A1AA] text-center animate-pulse">
                              {txStatus}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* NFT Characters Tab */}
        {activeTab === 'nfts' && (
          <>
            {/* NFT Info Banner */}
            <div className="glass-card p-4 mb-6 border border-[#9945FF]/30">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#9945FF] to-[#14F195] flex items-center justify-center">
                  <Gem className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-game text-sm text-white">GENESIS COLLECTION</h3>
                  <p className="text-xs text-[#A1A1AA]">
                    Unique characters with special abilities • $10.00 USD each • Limited supply
                  </p>
                  <p className="text-xs text-[#14F195] mt-1">
                    🎁 <span className="font-game">FOOT STABBER</span> - Free for all players!
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {nfts.map((nft) => {
                const isOwned = ownedNfts.includes(nft.id);
                const canAfford = tokenBalance >= nft.price;
                const isPurchasing = purchasingId === nft.id;
                const isMythic = nft.rarity === 'mythic';
                const isDefault = nft.is_default || nft.price_usd === 0;
                const hasCustomImage = nft.image_url && nft.image_url.length > 0;
                // Convert relative paths to full URLs for images
                const nftImageUrl = hasCustomImage 
                  ? (nft.image_url.startsWith('/') ? nft.image_url : nft.image_url)
                  : null;

                return (
                  <div
                    key={nft.id}
                    className={`shop-card relative ${isOwned || isDefault ? 'owned' : ''}`}
                    style={{ 
                      borderColor: isDefault ? '#14F195' : (isMythic ? '#FFD700' : nft.color),
                      boxShadow: (isOwned || isDefault) ? `0 0 20px ${isDefault ? '#14F195' : nft.color}40` : undefined
                    }}
                    data-testid={`nft-card-${nft.id}`}
                  >
                    {/* NFT Visual */}
                    <div 
                      className="aspect-square relative overflow-hidden"
                      style={{ 
                        background: hasCustomImage ? '#1a1a2e' : `linear-gradient(135deg, ${nft.color}30, ${nft.color}10)`,
                      }}
                    >
                      {/* Custom image for NFTs with image_url */}
                      {hasCustomImage ? (
                        <img 
                          src={nftImageUrl} 
                          alt={nft.name}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <>
                          {/* Animated Canvas character preview */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <NFTCharacterPreview 
                              nftId={nft.id} 
                              color={nft.color} 
                              size={160}
                            />
                          </div>
                        </>
                      )}

                      {/* Edition Badge - FREE for default, $10 for others */}
                      <div className={`absolute top-2 left-2 px-2 py-1 rounded text-[10px] font-game ${
                        isDefault ? 'bg-[#14F195] text-black animate-pulse' : 'bg-[#14F195] text-black'
                      }`}>
                        {isDefault ? '🎁 FREE' : '$10.00 USD'}
                      </div>

                      {/* Rarity Badge */}
                      <div 
                        className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-game uppercase ${
                          isMythic ? 'bg-gradient-to-r from-[#FFD700] to-[#FF6B00] text-black' : ''
                        }`}
                        style={!isMythic ? { 
                          backgroundColor: RARITY_COLORS[nft.rarity] + '30',
                          color: RARITY_COLORS[nft.rarity],
                          border: '1px solid ' + RARITY_COLORS[nft.rarity]
                        } : undefined}
                      >
                        {isMythic && <Sparkles className="w-3 h-3 inline mr-1" />}
                        {nft.rarity}
                      </div>

                      {/* Supply indicator */}
                      <div className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/50 text-[10px] text-white">
                        {isDefault || nft.supply === -1 ? '∞ Unlimited' : `${nft.supply} total`}
                      </div>

                      {(isOwned || isDefault) && (
                        <div className="absolute bottom-2 left-2 bg-[#14F195] text-black px-2 py-1 rounded text-xs font-game flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          {isDefault ? 'FREE' : 'OWNED'}
                        </div>
                      )}
                    </div>

                    {/* Info section */}
                    <div className="p-4">
                      <h3 className="font-game text-sm text-white mb-1">{nft.name}</h3>
                      <p className="text-[#A1A1AA] text-xs mb-3 line-clamp-2">{nft.description}</p>
                      
                      {/* Traits */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        <span 
                          className="px-2 py-0.5 rounded text-[10px]"
                          style={{ backgroundColor: nft.color + '30', color: nft.color }}
                        >
                          {nft.traits?.element}
                        </span>
                        <span className="px-2 py-0.5 rounded text-[10px] bg-[#333] text-[#A1A1AA]">
                          {nft.traits?.origin}
                        </span>
                      </div>

                      {/* Bonuses */}
                      <div className="space-y-1 mb-4 text-[10px]">
                        <div className="text-[#FFD700] font-game">SPECIAL ABILITY</div>
                        {Object.entries(nft.bonuses || {}).slice(0, 2).map(([key, value]) => (
                          <div key={key} className="flex justify-between text-[#A1A1AA]">
                            <span>{key.replace(/_/g, ' ')}</span>
                            <span className="text-[#14F195]">
                              {typeof value === 'boolean' ? (value ? '✓' : '✗') : `+${value}${typeof value === 'number' && key.includes('percent') ? '%' : ''}`}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Purchase or Equip button */}
                      {isOwned ? (
                        <div className="space-y-2">
                          {selectedNftId === nft.id ? (
                            <div className="w-full py-2 px-4 bg-[#14F195] border border-[#14F195] rounded text-center">
                              <span className="text-black font-game text-sm flex items-center justify-center gap-2">
                                <Check className="w-4 h-4" />
                                EQUIPPED
                              </span>
                            </div>
                          ) : (
                            <Button
                              onClick={() => onSelectNft && onSelectNft(nft)}
                              className="w-full bg-[#9945FF] hover:bg-[#8835EE] text-white"
                              data-testid={`equip-nft-${nft.id}`}
                            >
                              <Gem className="w-4 h-4 mr-2" />
                              EQUIP
                            </Button>
                          )}
                          <p className="text-[10px] text-[#A1A1AA] text-center">
                            {selectedNftId === nft.id ? 'Abilities active in game' : 'Click to use abilities'}
                          </p>
                        </div>
                      ) : isDefault ? (
                        // Free default NFT - always available to equip
                        <div className="space-y-2">
                          {selectedNftId === nft.id ? (
                            <div className="w-full py-2 px-4 bg-[#14F195] border border-[#14F195] rounded text-center">
                              <span className="text-black font-game text-sm flex items-center justify-center gap-2">
                                <Check className="w-4 h-4" />
                                EQUIPPED
                              </span>
                            </div>
                          ) : (
                            <Button
                              onClick={() => onSelectNft && onSelectNft(nft)}
                              className="w-full bg-[#14F195] hover:bg-[#14F195]/90 text-black"
                              data-testid={`equip-nft-${nft.id}`}
                            >
                              <Gem className="w-4 h-4 mr-2" />
                              EQUIP FREE
                            </Button>
                          )}
                          <p className="text-[10px] text-[#14F195] text-center font-game">
                            🎁 Free for all players!
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Button
                            onClick={() => handlePurchaseNft(nft)}
                            disabled={!canAfford || isPurchasing || isLoading || !publicKey}
                            className={`w-full ${canAfford && publicKey
                              ? 'bg-gradient-to-r from-[#9945FF] to-[#14F195] text-white hover:opacity-90' 
                              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            }`}
                            data-testid={`buy-nft-${nft.id}`}
                          >
                            {isPurchasing ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                MINTING...
                              </>
                            ) : canAfford ? (
                              <>
                                <Gem className="w-4 h-4 mr-2" />
                                {nft.price.toLocaleString()} SML
                              </>
                            ) : (
                              <>
                                <Lock className="w-4 h-4 mr-2" />
                                {nft.price.toLocaleString()} SML
                              </>
                            )}
                          </Button>
                          <p className="text-[10px] text-[#A1A1AA] text-center">
                            ≈ $10.00 USD at current rate
                          </p>
                          {isPurchasing && txStatus && (
                            <p className="text-xs text-[#A1A1AA] text-center animate-pulse">
                              {txStatus}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Achievements Tab */}
        {activeTab === 'achievements' && (
          <div className="space-y-6">
            {/* Player Stats Summary */}
            <div className="glass-card p-6">
              <h2 className="font-game text-lg text-[#FFD700] mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                YOUR PROGRESS
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#1a1a1a] rounded-lg p-4 text-center">
                  <div className="text-2xl font-game text-[#14F195]">{playerProgress.highestLevel}</div>
                  <div className="text-xs text-[#A1A1AA]">Highest Level</div>
                </div>
                <div className="bg-[#1a1a1a] rounded-lg p-4 text-center">
                  <div className="text-2xl font-game text-[#FF4444]">{playerProgress.totalKills.toLocaleString()}</div>
                  <div className="text-xs text-[#A1A1AA]">Total Kills</div>
                </div>
                <div className="bg-[#1a1a1a] rounded-lg p-4 text-center">
                  <div className="text-2xl font-game text-[#9945FF]">{playerProgress.highestScore.toLocaleString()}</div>
                  <div className="text-xs text-[#A1A1AA]">High Score</div>
                </div>
                <div className="bg-[#1a1a1a] rounded-lg p-4 text-center">
                  <div className="text-2xl font-game text-[#5AC8FA]">{playerProgress.totalGames}</div>
                  <div className="text-xs text-[#A1A1AA]">Games Played</div>
                </div>
              </div>
            </div>

            {/* Achievements Progress Summary */}
            <div className="glass-card p-4 flex items-center justify-between">
              <span className="text-[#A1A1AA]">Achievements Unlocked</span>
              <span className="font-game text-[#FFD700]">
                {achievements.filter(a => a.unlocked).length} / {achievements.length}
              </span>
            </div>

            {/* Skin Achievements */}
            <div>
              <h3 className="font-game text-sm text-[#9945FF] mb-4 flex items-center gap-2">
                <Palette className="w-4 h-4" />
                SKIN UNLOCKS
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {achievements.filter(a => a.reward_type === 'skin').map((achievement) => (
                  <div 
                    key={achievement.id}
                    className={`glass-card p-4 border-l-4 ${achievement.unlocked ? 'border-[#14F195]' : 'border-[#333]'}`}
                    data-testid={`achievement-${achievement.id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className={`font-game text-sm ${achievement.unlocked ? 'text-[#14F195]' : 'text-white'}`}>
                          {achievement.name}
                        </h4>
                        <p className="text-[#A1A1AA] text-xs">{achievement.description}</p>
                      </div>
                      <div 
                        className={`px-2 py-1 rounded text-[10px] font-game uppercase ${
                          achievement.unlocked 
                            ? 'bg-[#14F195]/20 text-[#14F195]' 
                            : 'bg-[#333] text-[#A1A1AA]'
                        }`}
                      >
                        {achievement.rarity}
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className={achievement.unlocked ? 'text-[#14F195]' : 'text-[#A1A1AA]'}>
                          {achievement.unlocked ? 'UNLOCKED!' : 'Progress'}
                        </span>
                        <span className="text-[#A1A1AA]">
                          {achievement.progress.toLocaleString()}/{achievement.requirement.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-[#333] rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all ${achievement.unlocked ? 'bg-[#14F195]' : 'bg-[#FFD700]'}`}
                          style={{ width: `${Math.min((achievement.progress / achievement.requirement) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Weapon Achievements */}
            <div>
              <h3 className="font-game text-sm text-[#FF4444] mb-4 flex items-center gap-2">
                <Crosshair className="w-4 h-4" />
                WEAPON UNLOCKS
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {achievements.filter(a => a.reward_type === 'weapon').map((achievement) => (
                  <div 
                    key={achievement.id}
                    className={`glass-card p-4 border-l-4 ${achievement.unlocked ? 'border-[#FF4444]' : 'border-[#333]'}`}
                    data-testid={`achievement-weapon-${achievement.id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className={`font-game text-sm ${achievement.unlocked ? 'text-[#FF4444]' : 'text-white'}`}>
                          {achievement.name}
                        </h4>
                        <p className="text-[#A1A1AA] text-xs">{achievement.description}</p>
                      </div>
                      <div 
                        className={`px-2 py-1 rounded text-[10px] font-game uppercase ${
                          achievement.unlocked 
                            ? 'bg-[#FF4444]/20 text-[#FF4444]' 
                            : 'bg-[#333] text-[#A1A1AA]'
                        }`}
                      >
                        {achievement.rarity}
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className={achievement.unlocked ? 'text-[#FF4444]' : 'text-[#A1A1AA]'}>
                          {achievement.unlocked ? 'UNLOCKED!' : 'Progress'}
                        </span>
                        <span className="text-[#A1A1AA]">
                          {achievement.progress.toLocaleString()}/{achievement.requirement.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-[#333] rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all ${achievement.unlocked ? 'bg-[#FF4444]' : 'bg-[#FFD700]'}`}
                          style={{ width: `${Math.min((achievement.progress / achievement.requirement) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tip */}
            <div className="glass-card p-4 text-center">
              <p className="text-[#A1A1AA] text-sm">
                <Star className="w-4 h-4 inline mr-2 text-[#FFD700]" />
                Unlocked skins and weapons appear in the <span className="text-[#9945FF] font-game">SKINS</span> and <span className="text-[#FF4444] font-game">WEAPONS</span> tabs
              </p>
            </div>
          </div>
        )}

        {/* Wallet Connection Notice */}
        {!publicKey && (
          <div className="mt-8 glass-card p-4 border border-[#FF9500]/40" data-testid="wallet-notice">
            <div className="flex items-center gap-3 justify-center">
              <Lock className="w-5 h-5 text-[#FF9500] flex-shrink-0" />
              <p className="text-sm text-[#A1A1AA]">
                Connect an external wallet to purchase items. 
                <span className="text-[#FF9500] font-game text-xs ml-1">Browsing is free!</span>
              </p>
            </div>
          </div>
        )}

        {/* Token Info */}
        <div className="mt-12 text-center space-y-2">
          <p className="text-[#52525B] text-sm">
            Token Contract: <span className="font-mono text-[#A1A1AA]">E6tBfgKTVCZ3ziP...pump</span>
          </p>
          <p className="text-[#52525B] text-xs">
            All purchases are verified on the Solana blockchain
          </p>
        </div>
      </div>
    </div>
  );
};

export default Shop;
